import { createBrowserRouter } from 'react-router-dom'
import { ProtectedRoute } from './protected-route'
import { SuspenseWrapper } from './suspense-wrapper'
import { RouteErrorBoundary } from '@/components/shared/error-boundary'
import { ROLES } from '@/lib/constants'
import { lazy } from 'react'

const LoginPage = lazy(() => import('@/pages/login-page').then((m) => ({ default: m.LoginPage })))
const RegisterPage = lazy(() => import('@/pages/register-page').then((m) => ({ default: m.RegisterPage })))
const RecoverPage = lazy(() => import('@/pages/recover-page').then((m) => ({ default: m.RecoverPage })))
const ActivatePage = lazy(() => import('@/pages/activate-page').then((m) => ({ default: m.ActivatePage })))
const NotFoundPage = lazy(() => import('@/pages/not-found-page').then((m) => ({ default: m.NotFoundPage })))
const AppLayout = lazy(() => import('@/components/layout/app-layout').then((m) => ({ default: m.AppLayout })))
const AgentsPage = lazy(() => import('@/pages/agents'))
const AgentFormPage = lazy(() => import('@/pages/agents/agent-form'))
const KnowledgeBasesPage = lazy(() => import('@/pages/knowledge-bases'))
const KbDetailPage = lazy(() => import('@/pages/knowledge-bases/kb-detail'))
const WorkflowsPage = lazy(() => import('@/pages/workflows'))
const WorkflowEditorPage = lazy(() => import('@/pages/workflows/editor'))
const TriggersPage = lazy(() => import('@/pages/triggers'))
const TriggerFormPage = lazy(() => import('@/pages/triggers/trigger-form-page'))
const CredentialsPage = lazy(() => import('@/pages/credentials'))
const FeatureTemplatesPage = lazy(() => import('@/pages/feature-templates'))
const FeatureTemplateFormPage = lazy(() => import('@/pages/feature-templates/feature-template-form-page'))
const MissionsPage = lazy(() => import('@/pages/missions'))
const MissionFormPage = lazy(() => import('@/pages/missions/mission-form-page'))
const MissionDetailPage = lazy(() => import('@/pages/missions/mission-detail-page'))
const SessionsPage = lazy(() => import('@/pages/sessions'))
const SessionDetailPage = lazy(() => import('@/pages/sessions/session-detail'))
const RunsPage = lazy(() => import('@/pages/runs'))
const RunDetailPage = lazy(() => import('@/pages/runs/run-detail'))
const InboxPage = lazy(() => import('@/pages/inbox'))
const UsagesPage = lazy(() => import('@/pages/usages'))
const ServicePricesPage = lazy(() => import('@/pages/service-prices'))
const MarkupRulesPage = lazy(() => import('@/pages/markup-rules'))
const ProvidersPage = lazy(() => import('@/pages/providers'))
const ProjectsPage = lazy(() => import('@/pages/projects'))
const UsersPage = lazy(() => import('@/pages/users'))
const IamTenantsPage = lazy(() => import('@/pages/iam/tenants'))
const ProfilePage = lazy(() => import('@/pages/profile-page').then((m) => ({ default: m.ProfilePage })))
const BillingPage = lazy(() => import('@/pages/billing-page').then((m) => ({ default: m.BillingPage })))
const DashboardPage = lazy(() => import('@/pages/dashboard'))

function Suspensed({ Component }: { Component: React.LazyExoticComponent<React.ComponentType> }) {
  return (
    <SuspenseWrapper>
      <Component />
    </SuspenseWrapper>
  )
}

export const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: <Suspensed Component={LoginPage} />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/register',
      element: <Suspensed Component={RegisterPage} />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/recover',
      element: <Suspensed Component={RecoverPage} />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/activate/:token',
      element: <Suspensed Component={ActivatePage} />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/',
      element: <ProtectedRoute />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          element: <Suspensed Component={AppLayout} />,
          errorElement: <RouteErrorBoundary />,
          children: [
            { index: true, element: <Suspensed Component={DashboardPage} /> },
            { path: 'dashboard', element: <Suspensed Component={DashboardPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'agents', element: <Suspensed Component={AgentsPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'agents/new', element: <Suspensed Component={AgentFormPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'agents/:id/edit', element: <Suspensed Component={AgentFormPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'knowledge-bases', element: <Suspensed Component={KnowledgeBasesPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'knowledge-bases/:id', element: <Suspensed Component={KbDetailPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'workflows', element: <Suspensed Component={WorkflowsPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'workflows/:id/edit', element: <Suspensed Component={WorkflowEditorPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'workflows/:workflowId/runs/:executionId', element: <Suspensed Component={RunDetailPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'runs', element: <Suspensed Component={RunsPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'inbox', element: <Suspensed Component={InboxPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'triggers', element: <Suspensed Component={TriggersPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'triggers/new', element: <Suspensed Component={TriggerFormPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'triggers/:id/edit', element: <Suspensed Component={TriggerFormPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'credentials', element: <Suspensed Component={CredentialsPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'feature-templates', element: <Suspensed Component={FeatureTemplatesPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'feature-templates/new', element: <Suspensed Component={FeatureTemplateFormPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'feature-templates/:id/edit', element: <Suspensed Component={FeatureTemplateFormPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'missions', element: <Suspensed Component={MissionsPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'missions/new', element: <Suspensed Component={MissionFormPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'missions/:id', element: <Suspensed Component={MissionDetailPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'missions/:id/edit', element: <Suspensed Component={MissionFormPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'sessions', element: <Suspensed Component={SessionsPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'sessions/:id', element: <Suspensed Component={SessionDetailPage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'usages', element: <Suspensed Component={UsagesPage} />, errorElement: <RouteErrorBoundary /> },
            {
              element: <ProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]} />,
              errorElement: <RouteErrorBoundary />,
              children: [
                { path: 'users', element: <Suspensed Component={UsersPage} />, errorElement: <RouteErrorBoundary /> },
              ],
            },
            {
              element: <ProtectedRoute requiredRoles={[ROLES.SUPER_ADMIN]} />,
              errorElement: <RouteErrorBoundary />,
              children: [
                { path: 'service-prices', element: <Suspensed Component={ServicePricesPage} />, errorElement: <RouteErrorBoundary /> },
                { path: 'markup-rules', element: <Suspensed Component={MarkupRulesPage} />, errorElement: <RouteErrorBoundary /> },
                { path: 'providers', element: <Suspensed Component={ProvidersPage} />, errorElement: <RouteErrorBoundary /> },
                { path: 'projects', element: <Suspensed Component={ProjectsPage} />, errorElement: <RouteErrorBoundary /> },
                { path: 'iam/tenants', element: <Suspensed Component={IamTenantsPage} />, errorElement: <RouteErrorBoundary /> },
              ],
            },
            { path: 'profile', element: <Suspensed Component={ProfilePage} />, errorElement: <RouteErrorBoundary /> },
            { path: 'billing', element: <Suspensed Component={BillingPage} />, errorElement: <RouteErrorBoundary /> },
            { path: '*', element: <Suspensed Component={NotFoundPage} /> },
          ],
        },
      ],
    },
  ],
  { basename: import.meta.env.BASE_URL.replace(/\/$/, '') || undefined },
)
